import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import { DataGridPro, GridColDef, GridRowId } from '@mui/x-data-grid-pro';

const useStyles = makeStyles(() => ({
    dataGrid: {
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #e0e0e0',
        },
        '& .MuiDataGrid-columnHeader': {
            padding: '8px 4px',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.875rem',
        },
        '& .MuiDataGrid-row': {
            backgroundColor: '#ffffff',
        },
        '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f8f9fa',
        },
        '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: '#e3f2fd',
        },
        '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: '#e3f2fd',
        },
        '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #e0e0e0',
        },
        '& .MuiDataGrid-cell.error-cell': {
            border: '2px solid #f44336 !important',
            borderRadius: '4px',
        },
        backgroundColor: '#ffffff',
    },
    /** Header “select all” is misleading for single-select; otherwise always shown. */
    hideHeaderSelectCheckbox: {
        '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
            display: 'none',
        },
    },
    dataGridSelectionDisabled: {
        '& .MuiDataGrid-checkboxInput': {
            color: '#ccc !important',
            cursor: 'not-allowed !important',
        },
        '& .MuiDataGrid-cellCheckbox': {
            cursor: 'not-allowed !important',
        },
        '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
            color: '#ccc !important',
            cursor: 'not-allowed !important',
        },
        '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root.Mui-disabled': {
            color: '#ccc !important',
        },
    },
}));

interface TagManagementDataGridProps {
    rows: any[];
    columns: GridColDef[];
    loading: boolean;
    selectedIds: GridRowId[];
    getRowId: (row: any) => string;
    onSelectionChange: (selectedIds: GridRowId[]) => void;
    onRowChanged: (row: any) => void;
    disabled: boolean;
    isCellEditableCustom?: (params: any) => boolean;
    selectionDisabled?: boolean;
    isRowSelectable?: (params: any) => boolean;
    singleSelect?: boolean;
}

export const TagManagementDataGrid: React.FC<TagManagementDataGridProps> = ({
    rows,
    columns,
    loading,
    selectedIds,
    getRowId,
    onSelectionChange,
    onRowChanged,
    disabled,
    selectionDisabled,
    isCellEditableCustom,
    isRowSelectable,
    singleSelect = false,
}) => {
    const classes = useStyles();

    const hideHeaderSelectCheckbox = singleSelect || selectionDisabled;

    const rowSelectionModel = useMemo(
        (): GridRowId[] => (selectionDisabled ? [] : [...selectedIds]),
        [selectionDisabled, selectedIds],
    );

    const processRowUpdate = (newRow: any, oldRow: any) => {
        const rowId = getRowId(newRow);
        const changedRow = rows.find((row) => getRowId(row) === rowId);
        if (!changedRow) {
            return newRow;
        }

        let updated = { ...changedRow };
        let hasChange = false;
        for (const key of Object.keys(newRow)) {
            if (key === 'id') {
                continue;
            }
            if (newRow[key] !== oldRow[key] && changedRow[key] !== newRow[key]) {
                updated = { ...updated, [key]: newRow[key] };
                hasChange = true;
            }
        }
        if (hasChange) {
            onRowChanged(updated);
        }
        return newRow;
    };

    const handleRowSelectionModelChange = (model: GridRowId[]) => {
        if (selectionDisabled) {
            return;
        }
        onSelectionChange([...model]);
    };

    return (
        <div
            style={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <DataGridPro
                rows={rows}
                columns={columns}
                getRowId={getRowId}
                pagination
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 25, page: 0 } },
                }}
                loading={loading}
                autoHeight={false}
                checkboxSelection
                disableRowSelectionOnClick
                disableMultipleRowSelection={singleSelect}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                processRowUpdate={processRowUpdate}
                isCellEditable={(params) =>
                    !disabled &&
                    (isCellEditableCustom ? isCellEditableCustom(params) : true)
                }
                isRowSelectable={isRowSelectable}
                disableColumnMenu={disabled}
                disableColumnFilter={disabled}
                disableColumnSelector={disabled}
                disableDensitySelector={disabled}
                className={`${classes.dataGrid} ${
                    selectionDisabled ? classes.dataGridSelectionDisabled : ''
                } ${
                    hideHeaderSelectCheckbox ? classes.hideHeaderSelectCheckbox : ''
                }`}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    height: '100%',
                }}
            />
        </div>
    );
};
